import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Bell, ChevronDown, Coins, Search } from "lucide-react";
import { cn } from "@/cn";
import { getPeopleUsage } from "@/api/people";
import { useProfile } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";

function initialsFrom(
  name?: string | null,
  email?: string | null,
): string {
  const src = (name ?? email ?? "?").trim();
  const parts = src.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return src.slice(0, 2).toUpperCase() || "?";
}

type SearchTarget = "people" | "companies" | "accounts" | "leads";

function searchTargetFromPath(pathname: string): SearchTarget {
  if (pathname.startsWith("/user/accounts") || pathname.startsWith("/companies")) {
    return pathname.startsWith("/user/accounts") ? "accounts" : "companies";
  }
  if (pathname.startsWith("/user/leads")) return "leads";
  return "people";
}

function searchRoute(target: SearchTarget): string {
  switch (target) {
    case "companies":
      return "/companies";
    case "accounts":
      return "/user/accounts";
    case "leads":
      return "/user/leads";
    default:
      return "/people";
  }
}

function searchPlaceholder(target: SearchTarget): string {
  switch (target) {
    case "companies":
      return "Search companies… ⌘K";
    case "accounts":
      return "Search accounts… ⌘K";
    case "leads":
      return "Search leads… ⌘K";
    default:
      return "Search people… ⌘K";
  }
}

export function GlobalSearch({
  className,
  autoFocus,
}: {
  className?: string;
  autoFocus?: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const target = searchTargetFromPath(location.pathname);
  const [q, setQ] = useState(() => searchParams.get("q") ?? "");
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync bar with route ?q= when navigating between directory pages.
  useEffect(() => {
    setQ(searchParams.get("q") ?? "");
  }, [location.pathname, searchParams]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  function onSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    const query = q.trim();
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    const base = searchRoute(target);
    navigate(`${base}${params.toString() ? `?${params}` : ""}`);
  }

  return (
    <form
      onSubmit={onSearchSubmit}
      className={cn("relative min-w-0", className)}
    >
      <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={searchPlaceholder(target)}
        className="w-full rounded-md border border-border bg-bg py-1.5 pl-8 pr-3 text-sm outline-none focus:border-brand"
        aria-label={searchPlaceholder(target).replace(" ⌘K", "")}
      />
    </form>
  );
}

export function CreditsChip({ className }: { className?: string }) {
  const { data: usage } = useQuery({
    queryKey: ["people-usage"],
    queryFn: getPeopleUsage,
    staleTime: 60_000,
    retry: false,
  });

  const creditsLabel = (() => {
    if (!usage?.connected) return "—";
    const n =
      usage.emailCredits ??
      usage.exportCredits ??
      usage.mobileCredits;
    if (n == null) return usage.label ?? "—";
    return n.toLocaleString();
  })();

  return (
    <Link
      to="/settings/credits"
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs text-foreground hover:bg-muted/20",
        className,
      )}
      title="Credits"
    >
      <Coins className="size-3.5 text-muted-foreground" />
      <span className="hidden sm:inline">Credits ·</span>
      <span className="font-medium tabular-nums">{creditsLabel}</span>
    </Link>
  );
}

export function NotificationsBell({ className }: { className?: string }) {
  return (
    <Link
      to="/settings/notifications"
      className={cn(
        "relative inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted/20 hover:text-foreground",
        className,
      )}
      title="Notifications"
      aria-label="Notifications"
    >
      <Bell className="size-4" />
      <span className="absolute right-1 top-1 size-1.5 rounded-full bg-transparent" />
    </Link>
  );
}

export function UserMenu({ className }: { className?: string }) {
  const { user, signOut } = useAuth();
  const { data: profile } = useProfile();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const displayName =
    profile?.name?.trim() ||
    user?.name?.trim() ||
    user?.email?.trim() ||
    "Account";
  const picture = profile?.profilePictureUrl;

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className={cn("relative shrink-0", className)} ref={menuRef}>
      <button
        type="button"
        onClick={() => setMenuOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 rounded-md px-1 py-0.5 hover:bg-muted/20"
        aria-expanded={menuOpen}
        aria-haspopup="menu"
      >
        {picture ? (
          <img
            src={picture}
            alt=""
            className="size-7 rounded-full object-cover"
          />
        ) : (
          <span className="inline-flex size-7 items-center justify-center rounded-full bg-brand/15 text-[11px] font-semibold text-brand">
            {initialsFrom(displayName, user?.email)}
          </span>
        )}
        <ChevronDown
          className={cn(
            "size-3.5 text-muted-foreground transition-transform",
            menuOpen && "rotate-180",
          )}
        />
      </button>
      {menuOpen && (
        <div
          role="menu"
          className="absolute right-0 mt-1 w-48 rounded-md border border-border bg-card py-1 shadow-lg"
        >
          <p className="truncate border-b border-border px-3 py-2 text-xs text-muted-foreground">
            {displayName}
          </p>
          <Link
            to="/settings/account"
            role="menuitem"
            className="block px-3 py-2 text-sm hover:bg-muted/20"
            onClick={() => setMenuOpen(false)}
          >
            Profile
          </Link>
          <Link
            to="/settings/integrations"
            role="menuitem"
            className="block px-3 py-2 text-sm hover:bg-muted/20"
            onClick={() => setMenuOpen(false)}
          >
            Integrations
          </Link>
          <button
            type="button"
            role="menuitem"
            className="block w-full px-3 py-2 text-left text-sm hover:bg-muted/20"
            onClick={() => {
              setMenuOpen(false);
              void signOut();
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

export function TopAppBar() {
  return (
    <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-3 border-b border-border bg-card/95 px-5 backdrop-blur supports-[backdrop-filter]:bg-card/80">
      <GlobalSearch className="mx-auto max-w-xl flex-1" />
      <CreditsChip />
      <NotificationsBell />
      <UserMenu />
    </header>
  );
}
